import { useEffect, useState } from 'react';
import { Workflow } from 'lucide-react';
import {
  apiClient,
  type ActiveFilter,
  type ApiError,
  type CaseStage,
  type CaseStagePhase,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import {
  Field,
  GlassSelect,
  TextInput,
} from '@/components/clients/form-primitives';
import { ConfigModal } from './config-modal';
import { ListToolbar } from './list-toolbar';

interface FormState {
  name: string;
  phase: CaseStagePhase;
  classification: string;
  order: number;
  active: boolean;
}

const EMPTY: FormState = {
  name: '',
  phase: 'judicial',
  classification: '',
  order: 0,
  active: true,
};

const PHASE_LABEL: Record<CaseStagePhase, string> = {
  judicial: 'Judicial',
  consultoria: 'Consultoria',
  administrativo: 'Administrativo',
  negociacao: 'Negociação',
  extrajudicial: 'Extrajudicial',
};

const PHASE_OPTIONS = (Object.keys(PHASE_LABEL) as CaseStagePhase[]).map((k) => ({
  value: k,
  label: PHASE_LABEL[k],
}));

export function CaseStagesPage() {
  const [items, setItems] = useState<CaseStage[]>([]);
  const [active, setActive] = useState<ActiveFilter>('true');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CaseStage | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .caseStagesList({ active, search: search.trim() || undefined })
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((e: ApiError) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, search]);

  function openNew() {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setModalOpen(true);
  }

  function openEdit(item: CaseStage) {
    setEditing(item);
    setForm({
      name: item.name,
      phase: item.phase,
      classification: item.classification ?? '',
      order: item.order,
      active: item.active,
    });
    setFormError(null);
    setModalOpen(true);
  }

  async function submit() {
    if (!form.name.trim()) {
      setFormError('Nome obrigatório');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const payload = {
        name: form.name.trim(),
        phase: form.phase,
        classification: form.classification.trim() || null,
        order: form.order,
        active: form.active,
      };
      if (editing) {
        await apiClient.caseStagesUpdate(editing.id, payload);
      } else {
        await apiClient.caseStagesCreate(payload);
      }
      setModalOpen(false);
      const rows = await apiClient.caseStagesList({
        active,
        search: search.trim() || undefined,
      });
      setItems(rows);
    } catch (e) {
      setFormError((e as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(item: CaseStage) {
    if (!window.confirm(`Excluir a etapa "${item.name}"?`)) return;
    try {
      await apiClient.caseStagesDelete(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      window.alert((e as ApiError).message);
    }
  }

  return (
    <div className="space-y-6">
      <ListToolbar
        title="Etapas"
        description="Etapas processuais agrupadas por fase (judicial, consultoria, administrativo…)."
        newLabel="Nova etapa"
        onNew={openNew}
        active={active}
        onActiveChange={setActive}
        search={search}
        onSearchChange={setSearch}
      />

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando…
          </p>
        ) : error ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">{error}</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <Workflow className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhuma etapa cadastrada.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Etapa</th>
                <th className="px-4 py-2 font-medium">Fase</th>
                <th className="px-4 py-2 font-medium">Classificação</th>
                <th className="px-4 py-2 font-medium">Ordem</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]"
                >
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      className="text-left font-medium text-[var(--fg)] hover:text-[var(--accent)]"
                    >
                      {item.name}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-[var(--fg-muted)]">
                    {PHASE_LABEL[item.phase]}
                  </td>
                  <td className="px-4 py-2 text-[var(--fg-muted)]">
                    {item.classification?.trim() || '—'}
                  </td>
                  <td className="px-4 py-2 text-[var(--fg-muted)]">
                    {item.order}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        item.active
                          ? 'inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-500'
                          : 'inline-flex items-center rounded-full bg-zinc-500/15 px-2 py-0.5 text-xs font-medium text-zinc-400'
                      }
                    >
                      {item.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item)}
                    >
                      Excluir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar etapa' : 'Nova etapa'}
        description="Etapa processual classificada por fase."
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? 'Salvando…' : 'Salvar'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {formError}
            </p>
          )}
          <Field label="Nome" required>
            <TextInput
              value={form.name}
              onChange={(e) =>
                setForm((s) => ({ ...s, name: e.target.value }))
              }
              placeholder="Ex: Aguardando Audiência"
              maxLength={160}
            />
          </Field>
          <Field label="Fase" required>
            <GlassSelect
              value={form.phase}
              onChange={(v) =>
                setForm((s) => ({ ...s, phase: v as CaseStagePhase }))
              }
              options={PHASE_OPTIONS}
            />
          </Field>
          <Field
            label="Classificação"
            hint="Subdivisão livre dentro da fase (opcional)."
          >
            <TextInput
              value={form.classification}
              onChange={(e) =>
                setForm((s) => ({ ...s, classification: e.target.value }))
              }
              placeholder="Opcional"
              maxLength={120}
            />
          </Field>
          <Field label="Ordem" hint="Posição dentro da fase (menor primeiro).">
            <TextInput
              type="number"
              min={0}
              max={9999}
              value={form.order}
              onChange={(e) =>
                setForm((s) => ({
                  ...s,
                  order: Number.isFinite(e.target.valueAsNumber)
                    ? e.target.valueAsNumber
                    : 0,
                }))
              }
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) =>
                setForm((s) => ({ ...s, active: e.target.checked }))
              }
              className="h-4 w-4 rounded border-[var(--border)]"
            />
            Ativo
          </label>
        </div>
      </ConfigModal>
    </div>
  );
}
